import { formatDateRu } from "../../../utils/appointmentLabels";
import { useMemo } from "react";
import {
  formatMembershipPeriod,
  membershipStatusMeta,
  membershipVisitsLine,
} from "../../../utils/clientMembershipUtils";
import ClientPayMembershipButton from "./ClientPayMembershipButton";
import s from "./clientApp.module.css";

function visitProgress(membership, stats) {
  const mv = stats?.membership_visits;
  if (mv?.is_visit_based && mv.total != null) {
    const remaining = mv.remaining ?? membership?.visits_remaining ?? 0;
    const used = mv.used ?? Math.max(0, mv.total - remaining);
    const pct = mv.total > 0 ? Math.round((used / mv.total) * 100) : 0;
    return { remaining, used, total: mv.total, pct };
  }
  if (membership?.visits_remaining != null) {
    return {
      remaining: membership.visits_remaining,
      used: null,
      total: null,
      pct: 0,
    };
  }
  return null;
}

function MembershipCard({ membership, stats, active, patientId }) {
  const status = membershipStatusMeta(membership.status);
  const visits = membershipVisitsLine(membership, stats);
  const period = formatMembershipPeriod(membership);
  const plan = membership.plan;
  const progress = visitProgress(membership, stats);
  const isVisitPlan = plan?.plan_type === "visits" && progress?.total != null;

  const facts = [
    period ? { label: "Период", value: period } : null,
    membership.ends_at && !period
      ? { label: "До", value: formatDateRu(membership.ends_at) }
      : null,
    plan?.price != null && Number(plan.price) > 0
      ? {
          label: "Стоимость",
          value: `${Number(plan.price).toLocaleString("ru-RU")} ₽`,
        }
      : null,
    stats?.attendance_pct != null
      ? { label: "Явка", value: `${stats.attendance_pct}%` }
      : null,
  ].filter(Boolean);

  return (
    <article className={`${s.profClubMem} ${active ? s.profClubMemActive : ""}`}>
      <div className={s.profClubMemHead}>
        <div className={s.profClubMemTitles}>
          <h3>{plan?.name || "Абонемент"}</h3>
          {isVisitPlan ? (
            <p className={s.profClubMemSub}>
              {progress.used} из {progress.total} использовано
            </p>
          ) : visits ? (
            <p className={s.profClubMemSub}>{visits}</p>
          ) : null}
        </div>
        <span className={`${s.profClubStatus} ${s[`profClubStatus_${status.tone}`]}`}>
          {status.label}
        </span>
      </div>

      {isVisitPlan ? (
        <div className={s.profClubMemProgress}>
          <div className={s.profClubMemProgressTop}>
            <span className={s.profClubMemRemain}>{progress.remaining}</span>
            <span className={s.profClubMemRemainLbl}>
              {progress.remaining === 1 ? "занятие" : "занятий"} осталось
            </span>
          </div>
          <div className={s.profClubMemBarTrack} aria-hidden>
            <div
              className={s.profClubMemBarFill}
              style={{ width: `${100 - progress.pct}%` }}
            />
          </div>
        </div>
      ) : null}

      {facts.length > 0 ? (
        <div className={s.profClubFactGrid}>
          {facts.map((fact) => (
            <div key={fact.label} className={s.profClubFactTile}>
              <span className={s.profClubFactTileLbl}>{fact.label}</span>
              <span className={s.profClubFactTileVal}>{fact.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      <ClientPayMembershipButton membership={membership} patientId={patientId} />
    </article>
  );
}

export default function ClientClubMembership({ dash, patientId }) {
  const membership = dash?.membership;
  const stats = dash?.stats;

  const list = useMemo(() => {
    const rows = dash?.memberships || [];
    const visible = rows.filter(
      (m) => m && m.status !== "cancelled" && m.status !== "expired",
    );
    if (visible.length) return visible;
    return membership ? [membership] : [];
  }, [dash?.memberships, membership]);

  return (
    <div className={s.profClub}>
      <p className={s.profClubIntro}>
        Тариф, срок действия и остаток занятий по абонементу
      </p>

      <section className={s.profClubSection}>
        <h2 className={s.profClubSectionTitle}>Мои абонементы</h2>
        {list.length === 0 ? (
          <p className={s.profMuted}>
            Абонемент не подключён. Обратитесь к администратору клуба.
          </p>
        ) : (
          <div className={s.profClubStack}>
            {list.map((m) => (
              <MembershipCard
                key={m.id}
                membership={m}
                stats={stats}
                active={membership?.id === m.id}
                patientId={patientId}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
